import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { ProjectArchitectureFlow } from '../diagrams/ProductArchitectureFlow';

const PROJECTS_PARENT_URL = `${process.env.PUBLIC_URL || ''}/projects/index.json`;

/** Homepage Work — PA Museum & Emission featured with architecture teasers. */
export default function SectionProjects() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(PROJECTS_PARENT_URL)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load work');
        return res.json();
      })
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !data) return null;

  const items = Array.isArray(data.items) ? data.items : [];
  if (items.length === 0) return null;

  return (
    <section
      id="featured-work"
      className="py-14 sm:py-20 border-t"
      style={{ background: 'var(--jb-forest-bg)', borderColor: 'var(--jb-border)' }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="max-w-2xl">
            <p className="font-mono text-xs text-cyan uppercase tracking-widest mb-2">Client delivery</p>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl md:text-4xl text-text-hero mb-2">
              Featured work
            </h2>
            <p className="font-body text-text-sub">
              {data.subtext || 'PA Museum and Emission Project — real systems in production.'}
            </p>
          </div>
          <a
            href="#work"
            className="inline-flex items-center gap-2 font-body font-semibold text-cyan text-sm hover:opacity-90"
          >
            View all work <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="space-y-6">
          {items.map((ref, i) => (
            <article
              key={ref.slug}
              className="rounded-2xl border overflow-hidden"
              style={{ borderColor: 'var(--jb-border)', background: 'var(--jb-forest-card)' }}
            >
              <div className={`grid lg:grid-cols-2 ${i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                <div className="p-6 sm:p-8 flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {ref.badge && (
                      <span
                        className="font-mono text-[0.65rem] uppercase tracking-wider px-2.5 py-1 rounded-full"
                        style={{ background: 'var(--jb-cyan-dim)', color: 'var(--jb-cyan)' }}
                      >
                        {ref.badge}
                      </span>
                    )}
                    {ref.client && (
                      <span className="font-body text-xs text-text-muted">{ref.client}</span>
                    )}
                  </div>
                  <h3 className="font-display font-extrabold text-xl sm:text-2xl text-text-hero mb-2">
                    {ref.title}
                  </h3>
                  <p className="font-body text-sm text-text-sub leading-relaxed mb-4">
                    {ref.description}
                  </p>
                  {Array.isArray(ref.tags) && (
                    <div className="flex flex-wrap gap-2 mb-5">
                      {ref.tags.map((tag) => (
                        <span
                          key={tag}
                          className="font-mono text-[0.65rem] uppercase tracking-wider px-2.5 py-1 rounded-full border"
                          style={{ borderColor: 'var(--jb-border)', color: 'var(--jb-text-sub)' }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <a
                    href={`#project/${ref.slug}`}
                    className="jb-cta inline-flex items-center gap-2 self-start px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan to-accent-green text-forest-bg font-body font-bold text-sm hover:opacity-90 transition"
                  >
                    View case study <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
                <div
                  className="p-4 sm:p-6 flex items-center border-t lg:border-t-0 lg:border-l"
                  style={{ borderColor: 'var(--jb-border)', background: 'var(--jb-forest-bg-2)' }}
                >
                  <div className="w-full">
                    <ProjectArchitectureFlow slug={ref.slug} compact />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
